import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { env } from "../constants/env";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { resetDatabase } from "../utils/offlineDB";
import SyncService from "../utils/SyncService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [isAuthReady, setIsAuthReady] = useState(false);

  // ----------------------------
  // TOKEN VALIDATION ON MOUNT
  // ----------------------------
  useEffect(() => {
    if (!token) {
      setIsAuthReady(true);
      navigate("/login");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        logout();
      } else {
        setIsAuthReady(true);
      }
    } catch (err) {
      logout();
    }
  }, [token]);

  // ---------------------------------------
  // LOGIN HANDLER (ONLINE ONLY)
  // ---------------------------------------
  const login = async ({ email, password }) => {
    if (!navigator.onLine) {
      toast.error("You must be online to log in");
      return;
    }

    try {
      const res = await fetch(`${env.kataApiUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed");

      // Store token + user
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);

      // Navigate based on role
      if (data.user.role === "HEALTH CENTER") {
        navigate("/laboratory-mapping/weekly-report");
      } else if (data.user.role === "ZAMMSA") {
        navigate("/laboratory-mapping/labs");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.message || "Login failed");
    }
  };

  // ---------------------------------------
  // LOGOUT
  // ---------------------------------------
  const logout = async () => {
    setToken(null);
    setUser(null);

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    SyncService.stop();
    await resetDatabase(); // IndexedDB / offline DB cleanup
    // Force reload to reset state
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ token, user, isAuthReady, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
